"use client";

import { cn } from "@/lib/utils";
import { useDirection } from "@radix-ui/react-direction";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { VariantProps, cva } from "class-variance-authority";
import * as React from "react";

const tabsListVariants = cva("inline-flex items-center justify-center", {
  variants: {
    variant: {
      default: "bg-muted p-1 rounded-md",
      underline: "h-full bg-transparent p-1 rounded-none overflow-hidden",
    },
    size: {
      sm: "text-xs sm:text-sm",
      base: "text-sm sm: text-base",
      lg: "text-base sm:text-lg",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "base",
  },
});

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "rounded-sm px-3 py-1.5 transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm",
        underline:
          "overflow-hidden px-1 py-2 border-b border-gray-300 text-gray-800 transition-colors hover:text-gray-700 data-[state=active]:border-primary data-[state=active]:border-b-2 data-[state=active]:text-primary",
      },
      size: {
        sm: "text-xs sm:text-sm",
        base: "text-sm sm:text-base",
        lg: "text-sm sm:text-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "sm",
    },
  }
);

const TabsVariantContext = React.createContext<{
  variant: "underline" | "default";
  size: "sm" | "lg" | "base";
}>({
  size: "sm",
  variant: "default",
});

interface TabsProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  variant?: "default" | "underline";
  size?: "sm" | "lg" | "base";
}

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(({ variant = "default", size = "base", ...props }, ref) => {
  const dir = useDirection();

  return (
    <TabsVariantContext.Provider value={{ size, variant }}>
      <TabsPrimitive.Root ref={ref} {...props} dir={dir} />
    </TabsVariantContext.Provider>
  );
});
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  Omit<
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
      VariantProps<typeof tabsListVariants>,
    "variant"
  >
>(({ className, ...props }, ref) => {
  const { size, variant } = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant, size }), className)}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  Omit<
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
      VariantProps<typeof tabsTriggerVariants>,
    "variant"
  >
>(({ className, ...props }, ref) => {
  const { size, variant } = React.useContext(TabsVariantContext);

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant, size }), className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
